import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button } from "@mui/material";
import TextField from "../../../components/SanitizedTextField/SanitizedTextField";
import {
  formatDisplayValue,
  formatUnitsRevisedDisplay,
  isNetMeterDocumentList,
  MAX_READING_DECIMALS,
  meterLabelFromDocument,
  mrcReadingsMatch,
  roundUnitsValue,
  sanitizeReadingInput,
  toNumberOrNull,
} from "../../BillingAndCollection/MeterReadingCorrection/mrcReadingUtils";
import MrcEvidencePreview from "../../BillingAndCollection/MeterReadingCorrection/MrcEvidencePreview";
import OpenBase64ImageInPopup from "../../../services/OpenBase64ImageInPopup";
import { handleFileUpload } from "../../../services/Functions";
import { documentExtensions } from "../../../utils/PortalName";
import "../../BillingAndCollection/MeterReadingCorrection/MeterReadingCorrectionForm.scss";
import "./DcoRemovedMeterReadingTable.scss";

const DIAL_LABEL = {
  1: "KWH",
  2: "KVARH",
  3: "MDI",
};

const MAX_MDI_READING_DECIMALS = 3;

const readingDecimalPlaces = (isMdi) =>
  isMdi ? MAX_MDI_READING_DECIMALS : MAX_READING_DECIMALS;

const roundReadingByType = (n, isMdi) =>
  Number(n.toFixed(readingDecimalPlaces(isMdi)));

const normalizeDialType = (item = {}) => {
  const raw = String(
    item.meterDialType || item.meterType || item.meterLabel || item.METER_TYPE || "",
  ).trim();
  if (/kvarh/i.test(raw)) return "KVARH";
  if (/mdi/i.test(raw)) return "MDI";
  if (/kwh/i.test(raw)) return "KWH";
  const meterCode = String(item.METER_CODE || item.meterCode || "");
  if (meterCode.includes("06")) return "KVARH";
  if (meterCode.includes("08")) return "MDI";
  return DIAL_LABEL[Number(item.meterId ?? item.METER_ID ?? item.removeMeterId)] || raw || "KWH";
};

const peakOffPeakBand = (item, { isTou = false } = {}) => {
  const code = String(item.meterCode ?? item.METER_CODE ?? "").trim().toLowerCase();
  if (code === "050600" || code.endsWith("0600") || code.endsWith("0506") || (code.includes("peak") && !code.includes("off"))) {
    return "Peak";
  }
  if (code === "050500" || code.endsWith("0500") || code.endsWith("0505") || code.includes("off")) {
    return "Off Peak";
  }
  const setNo = Number(item.setNo ?? item.SET_NO ?? item.removeSetNo ?? 1);
  if (isTou && Number.isFinite(setNo) && setNo > 0) {
    return setNo % 2 === 1 ? "Off Peak" : "Peak";
  }
  return "";
};

export const mapDcoRemovedMeterUnits = (units = [], options = {}) => {
  const list = Array.isArray(units) ? units : [];
  const tariff = String(options.tariffCode ?? options.tariffCd ?? "").trim();
  const isNetMeter = tariff === "12" || isNetMeterDocumentList(list);
  const kwhSetCount = new Set(
    list
      .filter((item) => normalizeDialType(item) === "KWH")
      .map((item) => Number(item.setNo ?? item.SET_NO ?? item.removeSetNo ?? 1))
      .filter((n) => Number.isFinite(n) && n > 0),
  ).size;
  const hasTouCode = list.some((item) => Boolean(peakOffPeakBand(item)));
  const isTou = isNetMeter || kwhSetCount >= 2 || hasTouCode;

  return list.map((item, index) => {
    const meterId = item.meterId ?? item.METER_ID ?? item.removeMeterId ?? "";
    const setNo = item.setNo ?? item.SET_NO ?? item.removeSetNo ?? 1;
    const mappedItem = {
      ...item,
      meterId,
      METER_ID: item.METER_ID ?? meterId,
      setNo,
      SET_NO: item.SET_NO ?? setNo,
    };
    const doc = {
      ...mappedItem,
      meterCode: item.meterCode ?? item.METER_CODE,
      METER_CODE: item.METER_CODE ?? item.meterCode,
    };
    let meterDialType = meterLabelFromDocument(doc, {
      isNetMeter,
      isTou,
      hasTouKwh: isTou,
      kwhSetCount,
    });
    if (!meterDialType || meterDialType === "—") {
      const dial = normalizeDialType(mappedItem);
      const band = peakOffPeakBand(mappedItem, { isTou });
      meterDialType = band ? `${dial} ${band}` : dial;
    }
    const isMdi =
      Number(meterId) === 3
      || /mdi/i.test(String(meterDialType))
      || normalizeDialType(mappedItem) === "MDI";
    const meterCodeKey = String(item.meterCode ?? item.METER_CODE ?? "").trim();
    const rawId = item.id ?? item.Id;
    const id = rawId != null && String(rawId).trim() !== ""
      ? `${setNo}-${meterId || "m"}-${meterCodeKey || rawId}-${index}`
      : `${setNo}-${meterId || "m"}-${meterCodeKey || index}`;
    return {
      id,
      setNo,
      isMdi,
      meterId,
      meterCode: item.meterCode ?? item.METER_CODE ?? "",
      meterDialType,
      serialNo:
        item.serialNo
        || item.meterSerialNo
        || item.removeMeterSerial
        || item.SERIAL_NO
        || item.METER_SERIAL_NO
        || "",
      feederNo: item.feederNo || item.feederNumber || item.FEEDER_NO || item.FEEDER_CODE || "",
      mf:
        item.mf
        ?? item.MF
        ?? item.multiplyingFactor
        ?? item.removedMultiplyingFactor
        ?? item.MULTIPLY_FACTOR
        ?? "",
      roundingCompCode:
        item.roundingCompCode
        ?? item.ROUNDING_COMP_CODE
        ?? item.ROUND_COM_CODE
        ?? item.removeRoundComCode
        ?? "",
      masterReading:
        item.masterReading
        ?? item.MASTER_READING
        ?? item.removeMeterMasterReading
        ?? item.previousReading
        ?? item.METER_READING
        ?? "",
      removedMeterReading:
        item.removedMeterReading
        ?? item.removeMeterReading
        ?? item.meterReading
        ?? "",
      mtReadings:
        item.mtReadings
        ?? item["m&tReadings"]
        ?? item.mAndTReadings
        ?? "",
      finalApprovedReading:
        item.finalApprovedReading
        ?? item.FINAL_APPROVED_READING
        ?? item.finalReading
        ?? "",
      evidence:
        [item.evidence, item.evidenceImage, item.removeMeterImages, item.removeMeterImage, item.removeMeterImg, item.imageSrc]
          .find((value) => {
            if (value == null) return false;
            const text = String(value).trim();
            return text !== "" && text !== "null" && text !== "undefined";
          })
        || "",
    };
  });
};

/** Round for display/API — KWH/KVARH: 2 dp; MDI: 3 dp. */
const normalizeRemovedReadingValue = (value, { isMdi = false } = {}) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  const n = toNumberOrNull(trimmed);
  if (n === null) return trimmed;
  const maxDecimals = readingDecimalPlaces(isMdi);
  const rounded = roundReadingByType(n, isMdi);
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(maxDecimals);
};

const formatRemovedReadingDisplay = (value, { isMdi = false } = {}) => {
  const normalized = normalizeRemovedReadingValue(value, { isMdi });
  if (!normalized) return "—";
  return normalized;
};

const calcUnitsConsumed = (removedReading, masterReading, mf, isMdi = false) => {
  const removed = toNumberOrNull(normalizeRemovedReadingValue(removedReading, { isMdi }));
  const master = toNumberOrNull(normalizeRemovedReadingValue(masterReading, { isMdi }));
  if (removed === null || master === null) return null;
  const factor = toNumberOrNull(mf);
  const multiplier = factor === null || factor === 0 ? 1 : factor;
  return roundUnitsValue((removed - master) * multiplier);
};

const readingErrorForRow = (
  value,
  masterReading,
  { required = true, isMdi = false, label = "Removed Meter Reading", compareToMaster } = {},
) => {
  if (String(value ?? "").trim() === "") {
    return required ? `${label} is required.` : "";
  }
  const n = toNumberOrNull(normalizeRemovedReadingValue(value, { isMdi }));
  if (n === null) return "Must be numeric.";
  const shouldCompare = compareToMaster ?? !isMdi;
  if (!shouldCompare) return "";
  const master = toNumberOrNull(normalizeRemovedReadingValue(masterReading, { isMdi: false }));
  if (master != null && !mrcReadingsMatch(n, master) && n < master) {
    return "Must be greater than or equal to Master Reading.";
  }
  return "";
};

const DcoRemovedMeterReadingTable = forwardRef(({
  units = [],
  readOnly = false,
  onCompleteChange,
  onValidationError,
  tariffCode = "",
  mode = "removed",
  showMtReadings = false,
}, ref) => {
  const isMandt = mode === "mandt";
  const isForward = mode === "forward";
  const showEvidence = !isMandt;
  const showMtColumn = isForward && Boolean(showMtReadings);
  const readingLabel = isForward
    ? "Final Approved Reading"
    : isMandt
      ? "M & T Reading"
      : "Removed Meter Reading";
  const rows = useMemo(() => mapDcoRemovedMeterUnits(units, { tariffCode }), [units, tariffCode]);
  const [readings, setReadings] = useState({});
  const [readingErrors, setReadingErrors] = useState({});
  const [evidenceById, setEvidenceById] = useState({});
  const [evidenceErrors, setEvidenceErrors] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const nextReadings = {};
    const nextEvidence = {};
    rows.forEach((row) => {
      let sourceReading = row.removedMeterReading;
      if (isForward) {
        sourceReading = readOnly ? (row.finalApprovedReading ?? "") : "";
      } else if (isMandt) {
        const hasMtValue = String(row.mtReadings ?? "").trim() !== "";
        sourceReading = readOnly && !hasMtValue ? row.removedMeterReading : row.mtReadings;
      }
      nextReadings[row.id] = sourceReading != null && sourceReading !== ""
        ? normalizeRemovedReadingValue(sourceReading, { isMdi: row.isMdi })
        : "";
      if (row.evidence) {
        nextEvidence[row.id] = { file: row.evidence, thumbnail: row.evidence };
      }
    });
    setReadings(nextReadings);
    setEvidenceById(nextEvidence);
    setReadingErrors({});
    setEvidenceErrors({});
    setPreview(null);
  }, [rows, isMandt, isForward, showMtColumn, readOnly]);

  const isFormComplete = useMemo(() => {
    if (readOnly || !rows.length) return false;
    return rows.every((row) => {
      const readingErr = readingErrorForRow(readings[row.id] ?? "", row.masterReading, {
        isMdi: row.isMdi,
        label: readingLabel,
        compareToMaster: isForward || isMandt || !row.isMdi,
      });
      if (readingErr) return false;
      if (isMandt || isForward) return true;
      const evidence = evidenceById[row.id];
      const evidenceSrc = evidence?.thumbnail || evidence?.file || "";
      return Boolean(String(evidenceSrc).trim());
    });
  }, [readOnly, rows, readings, evidenceById, isMandt, isForward, readingLabel]);

  useEffect(() => {
    onCompleteChange?.(isFormComplete);
  }, [isFormComplete, onCompleteChange]);

  const emitLiveReadingErrors = (nextReadings) => {
    const nextErrors = {};
    rows.forEach((row) => {
      const err = readingErrorForRow(nextReadings[row.id] ?? "", row.masterReading, {
        required: false,
        isMdi: row.isMdi,
        label: readingLabel,
        compareToMaster: isForward || isMandt || !row.isMdi,
      });
      if (err) nextErrors[row.id] = err;
    });
    setReadingErrors(nextErrors);
    onValidationError?.(Object.values(nextErrors).find(Boolean) || "");
  };

  const handleReadingChange = (row, value) => {
    const nextValue = sanitizeReadingInput(value, readingDecimalPlaces(row.isMdi));
    const nextReadings = { ...readings, [row.id]: nextValue };
    setReadings(nextReadings);
    emitLiveReadingErrors(nextReadings);
  };

  const handleReadingBlur = (row) => {
    const current = readings[row.id] ?? "";
    const normalized = normalizeRemovedReadingValue(current, { isMdi: row.isMdi });
    const nextReadings = { ...readings, [row.id]: normalized };
    setReadings(nextReadings);
    emitLiveReadingErrors(nextReadings);
  };

  const handleEvidenceUpload = (row, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const newFileName = `dco-removed-meter-${row.id}.${file.name.split(".").pop()}`;
    handleFileUpload(
      event,
      () => { },
      (val) => {
        setEvidenceById((prev) => ({
          ...prev,
          [row.id]: { ...(prev[row.id] || {}), file: val, thumbnail: prev[row.id]?.thumbnail || "" },
        }));
      },
      (err) => {
        setEvidenceErrors((prev) => {
          const next = { ...prev };
          if (err) next[row.id] = err;
          else delete next[row.id];
          return next;
        });
      },
      (thumb) => {
        setEvidenceById((prev) => ({
          ...prev,
          [row.id]: { file: prev[row.id]?.file || "", thumbnail: thumb || "" },
        }));
        setEvidenceErrors((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
      },
      newFileName,
    );
  };

  const clearEvidence = (rowId) => {
    setEvidenceById((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setEvidenceErrors((prev) => ({
      ...prev,
      [rowId]: "Evidence is required.",
    }));
  };

  const buildSubmitRows = (readingMap = readings, evidenceMap = evidenceById) =>
    rows.map((row) => {
      const readingValue = normalizeRemovedReadingValue(readingMap[row.id] ?? "", {
        isMdi: row.isMdi,
      });
      const evidence = evidenceMap[row.id];
      return {
        id: row.id,
        setNo: row.setNo,
        isMdi: Boolean(row.isMdi),
        meterId: row.meterId,
        meterCode: row.meterCode,
        meterDialType: row.meterDialType,
        serialNo: row.serialNo,
        feederNo: row.feederNo,
        mf: row.mf,
        roundingCompCode: row.roundingCompCode,
        masterReading: row.masterReading,
        removedMeterReading: isForward
          ? normalizeRemovedReadingValue(row.removedMeterReading, { isMdi: row.isMdi })
          : readingValue,
        mtReadings: isForward
          ? normalizeRemovedReadingValue(row.mtReadings, { isMdi: row.isMdi })
          : readingValue,
        finalApprovedReading: isForward ? readingValue : "",
        unitsConsumed: calcUnitsConsumed(
          readingValue,
          row.masterReading,
          row.mf,
          row.isMdi,
        ),
        evidence: evidence?.thumbnail || evidence?.file || "",
      };
    });

  const validate = () => {
    if (readOnly) {
      return { ok: false, message: "Meter readings cannot be edited.", rows: [] };
    }
    if (!rows.length) {
      return { ok: false, message: "Meter units not loaded.", rows: [] };
    }
    const nextReadingErrors = {};
    const nextEvidenceErrors = {};
    let ok = true;
    rows.forEach((row) => {
      const readingValue = normalizeRemovedReadingValue(readings[row.id] ?? "", {
        isMdi: row.isMdi,
      });
      const readingErr = readingErrorForRow(readingValue, row.masterReading, {
        isMdi: row.isMdi,
        label: readingLabel,
        compareToMaster: isForward || isMandt || !row.isMdi,
      });
      if (readingErr) {
        nextReadingErrors[row.id] = readingErr;
        ok = false;
      }
      if (!isMandt && !isForward) {
        const evidence = evidenceById[row.id];
        const evidenceSrc = evidence?.thumbnail || evidence?.file || "";
        if (!String(evidenceSrc).trim()) {
          nextEvidenceErrors[row.id] = "Evidence is required.";
          ok = false;
        }
      }
    });
    setReadingErrors(nextReadingErrors);
    setEvidenceErrors(nextEvidenceErrors);
    return {
      ok,
      message: ok
        ? ""
        : Object.values(nextReadingErrors).find(Boolean)
        || Object.values(nextEvidenceErrors).find(Boolean)
        || (isForward
          ? "Enter Final Approved Reading for every meter (KWH, KVARH, MDI)."
          : isMandt
            ? "Enter M & T Reading for every meter (KWH, KVARH, MDI)."
            : "Enter Removed Meter Reading and upload Evidence for every meter (KWH, KVARH, MDI)."),
      rows: ok ? buildSubmitRows() : [],
    };
  };

  useImperativeHandle(ref, () => ({
    validate,
    getSubmitRows: () => buildSubmitRows(),
  }));

  const colCount = 8 + (showMtColumn ? 1 : 0) + (isForward ? 1 : 0) + (showEvidence ? 1 : 0);
  const colgroup = (
    <colgroup>
      <col className="col-set" />
      <col className="col-meter" />
      <col className="col-serial" />
      <col className="col-feeder" />
      <col className="col-mf" />
      <col className="col-num" />
      <col className="col-removed" />
      {showMtColumn ? <col className="col-mt" /> : null}
      {isForward ? <col className="col-final" /> : null}
      <col className="col-num" />
      {showEvidence ? <col className="col-image" /> : null}
    </colgroup>
  );

  return (
    <div className={`mrc-documents-reading-table mrc-documents-reading-table--full dco-removed-meter-table${isMandt ? " dco-removed-meter-table--mandt" : ""}${isForward ? " dco-removed-meter-table--forward" : ""}`}>
      <div className="mrc-meter-table">
        <div className="mrc-meter-table__head">
          <table>
            {colgroup}
            <thead>
              <tr>
                <th className="th-multiline">Set<br />No</th>
                <th className="th-multiline">Meter Dial Type</th>
                <th className="th-multiline">Serial<br />No</th>
                <th className="th-multiline">Feeder<br />No</th>
                <th>MF</th>
                <th className="th-multiline">Master<br />Reading</th>
                {isForward ? (
                  // <th className="th-multiline">LS (D&amp;R) /<br />Remove Meter Reading</th>
                  <th className="th-multiline">Remove Meter Reading</th>

                ) : (
                  <th className="th-multiline">{isMandt ? <>M &amp; T<br />Readings{readOnly ? "" : " *"}</> : <>Removed Meter<br />Reading{readOnly ? "" : " *"}</>}</th>
                )}
                {showMtColumn ? <th className="th-multiline">M&amp;T<br />Reading</th> : null}
                {isForward ? <th className="th-multiline">Final Approved<br />Reading{readOnly ? "" : " *"}</th> : null}
                <th className="th-multiline">Units<br />Consumed</th>
                {showEvidence ? (
                  <th>{readOnly || isForward ? "Evidence" : "Evidence *"}</th>
                ) : null}
              </tr>
            </thead>
          </table>
        </div>
        <div className="mrc-meter-table__body">
          <table>
            {colgroup}
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={colCount}>
                    <p className="mrc-meter-hint">
                      Meter units will load here. All dial types (KWH, KVARH, MDI) with Peak / Off Peak will appear as rows.
                    </p>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const readingValue = readings[row.id] ?? "";
                  const readingError = readingErrors[row.id] || "";
                  const evidence = evidenceById[row.id];
                  const evidenceSrc = evidence?.thumbnail || "";
                  const evidenceError = evidenceErrors[row.id] || "";
                  const unitsConsumed = calcUnitsConsumed(
                    readingValue,
                    row.masterReading,
                    row.mf,
                    row.isMdi,
                  );
                  const isNewSet = index === 0 || rows[index - 1].setNo !== row.setNo;
                  const isMdi = Boolean(row.isMdi);

                  return (
                    <tr
                      key={row.id}
                      className={isNewSet ? "row-set-start" : ""}
                    >
                      <td className="cell-text cell-set">
                        <span className={`set-badge${isNewSet ? "" : " set-badge--muted"}`}>
                          {formatDisplayValue(row.setNo)}
                        </span>
                      </td>
                      <td className="cell-text cell-meter">
                        <span className="meter-type-pill">{row.meterDialType}</span>
                      </td>
                      <td className="cell-text cell-serial">
                        {formatDisplayValue(row.serialNo)}
                      </td>
                      <td className="cell-text cell-serial">
                        {formatDisplayValue(row.feederNo)}
                      </td>
                      <td className="cell-text cell-mf">
                        {formatDisplayValue(row.mf)}
                      </td>
                      <td className="cell-text cell-num">
                        {isMdi
                          ? formatRemovedReadingDisplay(row.masterReading, { isMdi: true })
                          : formatUnitsRevisedDisplay(row.masterReading)}
                      </td>
                      {isForward ? (
                        <td className="cell-text cell-num">
                          {formatRemovedReadingDisplay(row.removedMeterReading, { isMdi })}
                        </td>
                      ) : (
                        <td className="cell-site-survey">
                          {readOnly ? (
                            formatRemovedReadingDisplay(readingValue, { isMdi })
                          ) : (
                            <div className="mrc-meter-claimed-wrap">
                              <TextField
                                fullWidth
                                size="small"
                                value={readingValue}
                                onChange={(e) => handleReadingChange(row, e.target.value)}
                                onBlur={() => handleReadingBlur(row)}
                                placeholder="Enter reading"
                                className="textInputField mrc-meter-input mrc-meter-input--editable"
                                error={Boolean(readingError)}
                                required
                                inputProps={{
                                  inputMode: "decimal",
                                  "aria-label": `${readingLabel} set ${row.setNo} ${row.meterDialType}`,
                                }}
                              />
                              <div
                                className={`mrc-meter-error-slot${readingError ? " mrc-meter-error-slot--active" : ""}`}
                              >
                                {readingError || ""}
                              </div>
                            </div>
                          )}
                        </td>
                      )}
                      {showMtColumn ? (
                        <td className="cell-text cell-num">
                          {formatRemovedReadingDisplay(row.mtReadings, { isMdi })}
                        </td>
                      ) : null}
                      {isForward ? (
                        readOnly ? (
                          <td className="cell-text cell-num">
                            {formatRemovedReadingDisplay(readingValue || row.finalApprovedReading, { isMdi })}
                          </td>
                        ) : (
                          <td className="cell-site-survey">
                            <div className="mrc-meter-claimed-wrap">
                              <TextField
                                fullWidth
                                size="small"
                                value={readingValue}
                                onChange={(e) => handleReadingChange(row, e.target.value)}
                                onBlur={() => handleReadingBlur(row)}
                                placeholder="Enter reading"
                                className="textInputField mrc-meter-input mrc-meter-input--editable"
                                error={Boolean(readingError)}
                                required
                                inputProps={{
                                  inputMode: "decimal",
                                  "aria-label": `${readingLabel} set ${row.setNo} ${row.meterDialType}`,
                                }}
                              />
                              <div
                                className={`mrc-meter-error-slot${readingError ? " mrc-meter-error-slot--active" : ""}`}
                              >
                                {readingError || ""}
                              </div>
                            </div>
                          </td>
                        )
                      ) : null}
                      <td className="cell-text cell-num cell-calc">
                        {unitsConsumed == null ? "—" : formatUnitsRevisedDisplay(unitsConsumed)}
                      </td>
                      {showEvidence ? (
                      <td className="cell-site-image">
                        <div className="mrc-meter-image-wrap">
                          {evidenceSrc ? (
                            <div
                              className="mrc-meter-image-preview mrc-meter-image-preview--clickable"
                              role="button"
                              tabIndex={0}
                              title="Click to view"
                              onClick={() => setPreview({
                                src: evidenceSrc,
                                name: `${row.meterDialType} — Set ${row.setNo} Evidence`,
                              })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setPreview({
                                    src: evidenceSrc,
                                    name: `${row.meterDialType} — Set ${row.setNo} Evidence`,
                                  });
                                }
                              }}
                            >
                              <MrcEvidencePreview
                                src={evidenceSrc}
                                alt={`${row.meterDialType} evidence set ${row.setNo}`}
                              />
                              {!readOnly && !isMandt && !isForward ? (
                                <button
                                  type="button"
                                  className="mrc-meter-image-preview__clear"
                                  aria-label="Remove evidence"
                                  title="Remove to reupload"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    clearEvidence(row.id);
                                  }}
                                >
                                  <CloseIcon fontSize="inherit" />
                                </button>
                              ) : null}
                            </div>
                          ) : readOnly || isMandt || isForward ? (
                            <span className="mrc-image-na">—</span>
                          ) : (
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              startIcon={<FileUploadOutlinedIcon fontSize="small" />}
                              className={`mrc-meter-image-btn${evidenceError ? " mrc-meter-image-btn--error" : ""}`}
                              title={evidenceError || "Upload meter face photo"}
                            >
                              Upload
                              <input
                                hidden
                                accept={documentExtensions}
                                type="file"
                                onChange={(e) => handleEvidenceUpload(row, e)}
                              />
                            </Button>
                          )}
                          {!readOnly && !isMandt && !isForward ? (
                            <div
                              className={`mrc-meter-error-slot${evidenceError ? " mrc-meter-error-slot--active" : ""}`}
                            >
                              {evidenceError || ""}
                            </div>
                          ) : null}
                        </div>
                      </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {preview?.src ? (
        <OpenBase64ImageInPopup
          docVerifyPopUp={Boolean(preview?.src)}
          hideDocVerifyPopup={() => setPreview(null)}
          documentName={preview.name || "Evidence"}
          documentImage={preview.src}
          width="100%"
          document={preview.src}
          readOnly
          stableViewer
        />
      ) : null}
    </div>
  );
});

DcoRemovedMeterReadingTable.displayName = "DcoRemovedMeterReadingTable";

export default DcoRemovedMeterReadingTable;
